class HorizontalSlideshow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector(".horizontal-slideshow--track");
    this.slides = Array.from(this.querySelectorAll(".horizontal-slideshow--slide"));
    this.dotsWraps = Array.from(this.querySelectorAll(".horizontal-slideshow-dots"));
    this.dots = Array.from(this.querySelectorAll(".horizontal-slideshow-dots .dot"));

    this.index = 0;
    this.isDragging = false;
    this.startX = 0;
    this.currentX = 0;
    this.dragDX = 0;

    this.autoplayEnabled = this.getAttribute("data-autoplay") === "true";
    this.autoplaySpeedSetting = parseInt(this.getAttribute("data-autoplay-speed"), 10);
    this.autoplaySpeed = this.autoplaySpeedSetting * 1000;

    // ping-pong direction: +1 forward, -1 backward
    // this.autoplayDir = 1;

    // temp wrap state
    this.wrapMode = null; // "next" | "prev" | null
    this.wrapAnimating = false;
    this.tempClone = null;

    this.update();

    // dots
    this.dots.forEach((dot, i) => {
      dot.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();

        const perSet = this.slides.length;
        const targetIndex = i % perSet;

        this.stopAutoplay();
        this.cleanupTempClone();
        this.goTo(targetIndex, true);


        this.startAutoplay();
      });
    });

    // pointer
    this.track.addEventListener("pointerdown", (e) => this.onDown(e));
    window.addEventListener("pointermove", (e) => this.onMove(e));
    window.addEventListener("pointerup", (e) => this.onUp(e));
    window.addEventListener("pointercancel", (e) => this.onUp(e));

    // transition end (used for wrap only)
    this.track.addEventListener("transitionend", () => this.onTransitionEnd());

    // hover pause
    this.addEventListener("mouseenter", () => this.stopAutoplay());
    this.addEventListener("mouseleave", () => this.startAutoplay());

    // visibility pause
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    });

    this.startAutoplay();
    window.addEventListener("resize", () => this.update());
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.cleanupTempClone();
  }

  update(animate = true) {
    if (!this.track) return;

    this.track.style.transition = animate ? "transform 450ms ease" : "none";
    this.track.style.transform = `translateX(${-this.index * 100}%)`;

    // dots state
    const perSet = this.slides.length;
    this.dots.forEach((d, i) => {
      const pos = i % perSet;
      d.classList.toggle("is-selected", pos === this.index);
      d.setAttribute("aria-current", pos === this.index ? "true" : "false");
    });
  }

  goTo(i, animate = true) {
    const max = this.slides.length - 1;
    this.index = Math.max(0, Math.min(i, max));
    this.update(animate);
  }

  // --- Autoplay (ping-pong) ---
  autoplayStep() {
    if (this.slides.length < 2) return;

    const last = this.slides.length - 1;

    if (this.index === last) {
      this.wrapNext();
    } else {
      this.index+=1;
      this.update(true);
    }
  }

  startAutoplay() {
    if (!this.autoplayEnabled) return;
    if (this.slides.length < 2) return;
    if (this.timer) return;

    this.timer = window.setInterval(() => this.autoplayStep(), this.autoplaySpeed);
  }

  stopAutoplay() {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = null;
  }

  // --- Wrap helpers (temporary clone, correct direction animation) ---
  cleanupTempClone() {
    if (this.tempClone && this.tempClone.parentNode) {
      this.tempClone.parentNode.removeChild(this.tempClone);
    }
    this.tempClone = null;
    this.wrapMode = null;
    this.wrapAnimating = false;

    // refresh base slides list (in case it was modified)
    this.slides = Array.from(this.querySelectorAll(".horizontal-slideshow--slide"));
  }

  wrapNext() {
    // last -> first with LEFT animation
    if (this.wrapAnimating) return;
    if (this.slides.length < 2) return;

    this.wrapAnimating = true;
    this.wrapMode = "next";

    const first = this.slides[0];
    this.tempClone = first.cloneNode(true);
    this.tempClone.dataset.tempClone = "true";
    this.tempClone.setAttribute("aria-hidden", "true");
    this.track.appendChild(this.tempClone);

    // animate from last to the appended clone (one more step left)
    this.track.style.transition = "transform 450ms ease";
    const last = this.slides.length - 1;
    this.track.style.transform = `translateX(${-(last + 1) * 100}%)`;
  }

  wrapPrev() {
    // first -> last with RIGHT animation
    if (this.wrapAnimating) return;
    if (this.slides.length < 2) return;

    this.wrapAnimating = true;
    this.wrapMode = "prev";

    const lastSlide = this.slides[this.slides.length - 1];
    this.tempClone = lastSlide.cloneNode(true);
    this.tempClone.dataset.tempClone = "true";
    this.tempClone.setAttribute("aria-hidden", "true");
    this.track.insertBefore(this.tempClone, this.track.firstChild);

    // keep showing the real first slide by shifting left instantly (-100%)
    this.track.style.transition = "none";
    this.track.style.transform = `translateX(-100%)`;

    // next frame: animate RIGHT to 0% (shows the prepended clone)
    requestAnimationFrame(() => {
      this.track.style.transition = "transform 450ms ease";
      this.track.style.transform = `translateX(0%)`;
    });
  }

  onTransitionEnd() {
    if (!this.wrapAnimating) return;

    if (this.wrapMode === "next") {
      // we are visually on the appended first-clone; snap to real first (index 0)
      this.track.style.transition = "none";
      this.index = 0;
      this.track.style.transform = `translateX(0%)`;

      this.cleanupTempClone();
      this.update(false);
    } else if (this.wrapMode === "prev") {
      // we are visually on the prepended last-clone; snap to real last
      const realLast = this.slides.length - 1;

      this.track.style.transition = "none";
      this.index = realLast;
      this.track.style.transform = `translateX(${-realLast * 100}%)`;

      this.cleanupTempClone();
      this.update(false);
    }
  }

  // --- Pointer logic ---
  onDown(e) {
    if (!e.isPrimary) return;
    if (e.target.closest(".horizontal-slideshow-dots, a, button")) return;

    this.isDragging = true;
    this.stopAutoplay();

    // don’t allow dragging while a wrap animation is in progress
    if (this.wrapAnimating) {
      this.isDragging = false;
      return;
    }

    this.cleanupTempClone();

    this.track.style.transition = "none";
    this.startX = e.clientX;
    this.currentX = e.clientX;
    this.dragDX = 0;

    this.track.setPointerCapture(e.pointerId);
  }

  onMove(e) {
    if (!this.isDragging) return;

    this.currentX = e.clientX;
    this.dragDX = this.currentX - this.startX;

    const width = this.getBoundingClientRect().width || 1;
    const dragPercent = (this.dragDX / width) * 100;

    // clamp so no white gaps during drag
    const raw = (-this.index * 100) + dragPercent;
    const min = -(this.slides.length - 1) * 100;
    const max = 0;
    const clamped = Math.max(min, Math.min(raw, max));

    this.track.style.transform = `translateX(${clamped}%)`;
  }

  onUp(e) {
    if (!this.isDragging) return;
    this.isDragging = false;

    const width = this.getBoundingClientRect().width || 1;
    const threshold = width * 0.15;
    const last = this.slides.length - 1;

    if (this.dragDX > threshold) {
      // swipe right => previous (wrap if at first)
      if (this.index === 0) {
        this.wrapPrev();
      } else {
        this.goTo(this.index - 1, true);
      }
    } else if (this.dragDX < -threshold) {
      // swipe left => next (wrap if at last)
      if (this.index === last) {
        this.wrapNext();
      } else {
        this.goTo(this.index + 1, true);
      }
    } else {
      this.update(true);
    }

    this.dragDX = 0;
    this.startAutoplay();
  }
}

customElements.define("horizontal-slideshow", HorizontalSlideshow);
